import { nullSafeGet, nullSafeGetOrElse } from './DataAccessUtils';
import { ROLE_TYPES } from './DataConstants';
import { checkAndFormatPhoneNumber, getInitials } from './DataFormatterUtils';
import { getAvatarStyle } from './ImageUtils';

function escapeForMapPopupText(value) {
	if (value == null || value === '') {
		return '';
	}
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

export interface LocationsMapPopupParts {
	titleHtml: string;
	bodyHtml: string;
}

// A single contact reduced to what the space-constrained map tooltip / side list can show.
// `nameGiven`/`nameFamily` are kept (alongside the joined `name`) so the tooltip can build the
// initials avatar and its deterministic colour the same way `ContactAvatar` does.
export interface CompactContactLine {
	name?: string;
	role?: string;
	phone?: string;
	email?: string;
	nameGiven?: string;
	nameFamily?: string;
}

// A labelled cluster of contacts (e.g. "Facilities Contact", "Technicians").
export interface LocationContactGroup {
	label: string;
	lines: CompactContactLine[];
}

export interface LocationContactGroupsOptions {
	// Internal-tech users additionally see the location's assigned field techs. Resolved contacts are
	// used when `fieldTechContacts` is populated; otherwise the parallel `fieldTechEmails` array.
	showTechnicians?: boolean;
}

// Map a resolved Contact (nameGiven/nameFamily/title/department/phoneWork/phoneMobile/email — see
// ContactCardDisplay) to a compact line. Prefers the work phone, falling back to mobile.
function toCompactContactLine(contact): CompactContactLine {
	const nameGiven = nullSafeGet('nameGiven', contact);
	const nameFamily = nullSafeGet('nameFamily', contact);
	const name = [nameGiven, nameFamily].filter(Boolean).join(' ');
	const role = [nullSafeGet('title', contact), nullSafeGet('department', contact)]
		.filter(Boolean)
		.join(', ');
	const phone = nullSafeGet('phoneWork', contact) || nullSafeGet('phoneMobile', contact);
	const email = nullSafeGet('email', contact);
	return { name, role, phone, email, nameGiven, nameFamily };
}

// Resolved Contact objects may sit at the record root or under `buyerFacility`, so check both.
// Empty root arrays are treated as absent — `[]` is truthy in JS and would block `||` fallback.
function resolveNonEmptyArrayFromRecordOrBuyerFacility(record, key: string): any[] | null {
	const rootValue = nullSafeGetOrElse(key, record, null);
	if (Array.isArray(rootValue) && rootValue.length > 0) {
		return rootValue;
	}
	const nestedValue = nullSafeGetOrElse(`buyerFacility.${key}`, record, null);
	if (Array.isArray(nestedValue) && nestedValue.length > 0) {
		return nestedValue;
	}
	return null;
}

// Resolved Contact objects may sit at the record root or under `buyerFacility`, so check both.
// Falls back to a single-contact field when the array form is absent.
function collectResolvedContacts(record, arrayKey: string, singleKey?: string) {
	const fromArray = resolveNonEmptyArrayFromRecordOrBuyerFacility(record, arrayKey);
	if (fromArray) {
		return fromArray;
	}
	if (singleKey) {
		const single =
			nullSafeGet(singleKey, record) || nullSafeGet(`buyerFacility.${singleKey}`, record);
		if (single) {
			return [single];
		}
	}
	return [];
}

function collectEmails(record, emailsKey: string): string[] {
	const emails = resolveNonEmptyArrayFromRecordOrBuyerFacility(record, emailsKey);
	return Array.isArray(emails) ? emails.filter(Boolean) : [];
}

// Build the compact lines for one contact slot. `*Emails` is the authoritative full list of
// addresses on the location; `*Contacts` is the best-effort *hydrated* subset (resolved Contact
// objects with name/title/phone) — it can be shorter than `*Emails` when some addresses have no
// matching Contact record, and the lite map projection returns ONLY `*Emails` (empty `*Contacts`).
// So we render EVERY email, hydrating each from its resolved Contact (matched by email) where one
// exists, and append any resolved Contact not represented by an address (incl. single-contact
// fields, e.g. `facilityInChargeContact`, and contacts with no/other email).
function buildContactLines(
	record,
	contactsArrayKey: string,
	emailsKey: string,
	contactsSingleKey?: string
): CompactContactLine[] {
	const resolved = collectResolvedContacts(record, contactsArrayKey, contactsSingleKey);
	const emails = collectEmails(record, emailsKey);

	const resolvedByEmail: { [email: string]: any } = {};
	resolved.forEach((contact) => {
		const contactEmail = nullSafeGet('email', contact);
		if (contactEmail) {
			resolvedByEmail[String(contactEmail).toLowerCase()] = contact;
		}
	});
	const emailKeys = new Set(emails.map((email) => String(email).toLowerCase()));

	const lines: CompactContactLine[] = emails.map((email) => {
		const match = resolvedByEmail[String(email).toLowerCase()];
		return match ? toCompactContactLine(match) : ({ email } as CompactContactLine);
	});
	resolved.forEach((contact) => {
		const contactEmail = nullSafeGet('email', contact);
		const key = contactEmail ? String(contactEmail).toLowerCase() : '';
		if (!key || !emailKeys.has(key)) {
			lines.push(toCompactContactLine(contact));
		}
	});

	return lines;
}

/**
 * Extract the contact groups to surface for a location, used by the fullscreen side list. Empty
 * groups are omitted, so callers can render the result directly without their own presence checks.
 * (The map tooltip uses the role-aware `buildPopupContactSections` below.)
 */
export function getLocationContactGroups(
	record,
	options: LocationContactGroupsOptions = {}
): LocationContactGroup[] {
	const groups: LocationContactGroup[] = [];

	const facilityLines = buildContactLines(
		record,
		'facilityInChargeContacts',
		'facilityInChargeEmails',
		'facilityInChargeContact'
	);
	if (facilityLines.length > 0) {
		groups.push({
			label: facilityLines.length > 1 ? 'Facilities Contacts' : 'Facilities Contact',
			lines: facilityLines,
		});
	}

	const primaryLines = buildContactLines(
		record,
		'primaryContactsContacts',
		'primaryContactsEmails'
	);
	if (primaryLines.length > 0) {
		groups.push({
			label: primaryLines.length > 1 ? 'Primary Contacts' : 'Primary Contact',
			lines: primaryLines,
		});
	}

	if (options.showTechnicians) {
		const technicianLines = buildContactLines(record, 'fieldTechContacts', 'fieldTechEmails');
		if (technicianLines.length > 0) {
			groups.push({
				label: technicianLines.length > 1 ? 'Technicians' : 'Technician',
				lines: technicianLines,
			});
		}
	}

	return groups;
}

export function getFormattedLocationPhoneNumber(record): string | null {
	const phoneNumber = nullSafeGet('phoneNumber', record);
	if (!phoneNumber) {
		return null;
	}
	return checkAndFormatPhoneNumber(phoneNumber);
}

// ── Map tooltip rendering ────────────────────────────────────────────────────────────────────
// The tooltip body is injected as an HTML string (see MapGalleryMarkerPopover) so it has no React
// event handlers — the "view more" expander is a native <details>/<summary> (styled in
// MapboxGallery.less) and every interactive affordance is a plain <a>/<details>.

// "Open in new" affordance shown beside the title; replaces the old "View location" link. Colour is
// inherited from the wrapping element via `currentColor` so it picks up the role link colour. The
// path mirrors the JSX `LocationExternalLinkIcon` used in the side list — keep the two in sync.
const EXTERNAL_LINK_ICON_SVG = `<svg width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" style="display:block"><path d="M1.07122 0.64447H10.9284C11.1654 0.64447 11.3569 0.835988 11.3569 1.07304V10.9302C11.3569 11.1672 11.1654 11.3588 10.9284 11.3588H6.10693C6.048 11.3588 5.99979 11.3105 5.99979 11.2516V10.5016C5.99979 10.4427 6.048 10.3945 6.10693 10.3945H10.3926V1.60876H1.60693V5.89447C1.60693 5.9534 1.55872 6.00161 1.49979 6.00161H0.74979C0.690862 6.00161 0.642647 5.9534 0.642647 5.89447V1.07304C0.642647 0.835988 0.834165 0.64447 1.07122 0.64447ZM4.10738 7.17215L3.40827 6.47304C3.39411 6.45881 3.38423 6.44089 3.37975 6.42132C3.37526 6.40175 3.37636 6.38131 3.38292 6.36233C3.38948 6.34336 3.40123 6.3266 3.41684 6.31397C3.43245 6.30135 3.45129 6.29335 3.47122 6.2909L5.8739 6.00965C5.9422 6.00161 6.00113 6.0592 5.99309 6.12885L5.71184 8.53152C5.70113 8.61992 5.59265 8.65742 5.5297 8.59447L4.82792 7.89269L1.39666 11.3239C1.35515 11.3655 1.28684 11.3655 1.24533 11.3239L0.677468 10.7561C0.63595 10.7146 0.63595 10.6463 0.677468 10.6047L4.10738 7.17215Z" fill="currentColor"/></svg>`;

const SEPARATOR_HTML = '<div class="my-3 border-0 border-t border-solid border-grey-4"></div>';

// Which contacts a given role surfaces in the tooltip.
type PopupContactVariant = 'buyer' | 'supplierInternal' | 'thirdParty';

// A labelled contact section split into the lines shown up-front (`outside`) and those folded into
// the "view more" expander (`overflow`).
interface PopupContactSection {
	label: string;
	outside: CompactContactLine[];
	overflow: CompactContactLine[];
}

/**
 * Build the ordered tooltip contact sections for a role:
 *  - buyer            → Primary Contact (1 outside) then Facilities Contact (1 outside)
 *  - supplierInternal → Technicians: the dispatcher's primary techs (`primaryTechList`) as "Primary
 *                       Technician" + the location's field techs (`fieldTechEmails`) as "Field Tech";
 *                       first 1–2 outside
 *  - thirdParty       → Client Contact (facilities-in-charge, 1 outside); no technician data
 * Everything past the outside slice is returned as `overflow` for the "view more" expander.
 */
function buildPopupContactSections(record, variant: PopupContactVariant): PopupContactSection[] {
	if (variant === 'supplierInternal') {
		// One "Technicians" list combining the dispatcher's primary technicians (the real "Primary
		// Technician" — `primaryTechList`/`primaryTechListContacts` on the LocationForSupplier
		// projection, NOT the location's Primary *Contacts*) and the assigned field techs (shown as
		// "Field Tech"; the backend never hydrates `fieldTechContacts`, so these are email-only). The
		// role label is forced per source. Primary techs lead so the up-front slot (max 2) prefers
		// them; field techs and any extra primary techs fall into "view more". When there are no
		// primary techs, field techs take the up-front slot.
		const primaryTechnicians = buildContactLines(
			record,
			'primaryTechListContacts',
			'primaryTechList'
		).map((line) => ({ ...line, role: 'Primary Technician' }));
		const fieldTechnicians = buildContactLines(record, 'fieldTechContacts', 'fieldTechEmails').map(
			(line) => ({ ...line, role: 'Field Tech' })
		);
		const technicians = [...primaryTechnicians, ...fieldTechnicians];
		const outsideCount = Math.min(primaryTechnicians.length || fieldTechnicians.length, 2);
		return [
			{
				label: 'Technicians',
				outside: technicians.slice(0, outsideCount),
				overflow: technicians.slice(outsideCount),
			},
		];
	}
	if (variant === 'thirdParty') {
		const clientContacts = buildContactLines(
			record,
			'facilityInChargeContacts',
			'facilityInChargeEmails',
			'facilityInChargeContact'
		);
		return [
			{
				label: 'Client Contact',
				outside: clientContacts.slice(0, 1),
				overflow: clientContacts.slice(1),
			},
		];
	}
	const primaryContacts = buildContactLines(
		record,
		'primaryContactsContacts',
		'primaryContactsEmails'
	);
	const facilitiesContacts = buildContactLines(
		record,
		'facilityInChargeContacts',
		'facilityInChargeEmails',
		'facilityInChargeContact'
	);
	return [
		{
			label: 'Primary Contact',
			outside: primaryContacts.slice(0, 1),
			overflow: primaryContacts.slice(1),
		},
		{
			label: 'Facilities Contact',
			outside: facilitiesContacts.slice(0, 1),
			overflow: facilitiesContacts.slice(1),
		},
	];
}

// Initials avatar mirroring ContactAvatar: same `getInitials` and deterministic `getAvatarStyle`
// colouring (seeded by the contact's name, falling back to the email for email-only lines).
function renderContactAvatarHtml(line: CompactContactLine): string {
	const initials =
		getInitials(line.nameGiven, line.nameFamily) ||
		(line.email ? line.email.charAt(0).toUpperCase() : '');
	const colorSeed = `${line.nameGiven || ''}${line.nameFamily || ''}` || line.email || '';
	const avatarStyle = getAvatarStyle(colorSeed);
	return `<div class="footnoteMedium flex h-9 w-9 shrink-0 items-center justify-center rounded-full" style="background-color:${
		avatarStyle.backgroundColor
	};color:${avatarStyle.color}">${escapeForMapPopupText(initials)}</div>`;
}

function renderContactLineHtml(line: CompactContactLine): string {
	const nameHtml = line.name
		? `<span class="text-character-primary">${escapeForMapPopupText(line.name)}</span>`
		: '';
	const roleHtml = line.role
		? `<span class="text-character-secondary">${line.name ? ', ' : ''}${escapeForMapPopupText(
				line.role
		  )}</span>`
		: '';
	const headerHtml =
		nameHtml || roleHtml
			? `<div class="bodyregular leading-snug">${nameHtml}${roleHtml}</div>`
			: '';
	const emailHtml = line.email
		? `<div class="footnoteregular text-character-secondary leading-snug break-all">${escapeForMapPopupText(
				line.email
		  )}</div>`
		: '';
	const phoneHtml = line.phone
		? `<div class="footnoteregular text-character-secondary leading-snug">${escapeForMapPopupText(
				checkAndFormatPhoneNumber(line.phone)
		  )}</div>`
		: '';
	const detailsHtml = `${headerHtml}${emailHtml}${phoneHtml}`;
	if (!detailsHtml) {
		return '';
	}
	return `<div class="mt-1 mb-3 flex items-center gap-2.5">${renderContactAvatarHtml(
		line
	)}<div class="min-w-0 flex-1">${detailsHtml}</div></div>`;
}

// One section = its label once, the up-front lines, then the overflow lines wrapped in a
// collapsible `.ow-map-popup__viewmore-content` block (revealed in place, under this same label,
// when the shared toggle is checked — so the heading never repeats). A section with no `outside`
// lines is rendered entirely inside the collapsible block (label + lines only show on expand).
function renderContactSectionHtml(section: PopupContactSection, withTopMargin: boolean): string {
	const outsideHtml = section.outside.map(renderContactLineHtml).join('');
	const overflowHtml = section.overflow.map(renderContactLineHtml).join('');
	if (!outsideHtml && !overflowHtml) {
		return '';
	}
	const labelHtml = `<div class="bodyregular text-character-primary${
		withTopMargin ? ' mt-3' : ''
	}">${escapeForMapPopupText(section.label)}</div>`;
	if (!outsideHtml) {
		return `<div class="ow-map-popup__viewmore-content">${labelHtml}${overflowHtml}</div>`;
	}
	const overflowBlock = overflowHtml
		? `<div class="ow-map-popup__viewmore-content">${overflowHtml}</div>`
		: '';
	return `${labelHtml}${outsideHtml}${overflowBlock}`;
}

/**
 * Render the contact sections with a single "view more" expander. The body is an injected HTML
 * string (no React), so expand/collapse is a CSS checkbox hack: a hidden checkbox precedes the
 * sections, each section's overflow sits in a `.ow-map-popup__viewmore-content` block hidden until
 * `:checked`, and the toggle <label> is emitted last so "view less" sits at the bottom of all
 * content. `toggleId` keeps the input/label pairing unique per location popup.
 */
function renderContactsBlockHtml(
	sections: PopupContactSection[],
	linkColorClass: string,
	toggleId: string
): string {
	const renderableSections = sections.filter(
		(section) => section.outside.length > 0 || section.overflow.length > 0
	);
	const sectionsHtml = renderableSections
		.map((section, index) => renderContactSectionHtml(section, index > 0))
		.join('');
	if (!sectionsHtml) {
		return '';
	}
	// Anything collapsed? Either a visible section with overflow lines, or a fully-hidden section
	// (no `outside`, lines only on expand). Both end up in a `.ow-map-popup__viewmore-content` block.
	const hasHiddenContent = renderableSections.some((section) => section.overflow.length > 0);
	if (!hasHiddenContent) {
		return sectionsHtml;
	}
	const inputId = `ow-map-popup-viewmore-${toggleId}`;
	const labelHtml = `<label for="${inputId}" class="ow-map-popup__viewmore-label bodymedium ${linkColorClass}"><span class="ow-map-popup__viewmore-more bodyregular">view more</span><span class="ow-map-popup__viewmore-less bodyregular">view less</span></label>`;
	// The checkbox must precede every `.ow-map-popup__viewmore-content` sibling for the `~` selector
	// to reach them, so it leads the block; the label trails so "view less" lands at the bottom.
	return `<input type="checkbox" id="${inputId}" class="ow-map-popup__viewmore-toggle" />${sectionsHtml}${labelHtml}`;
}

function renderLocationTitleHtml(record, detailHref: string, linkColorClass: string): string {
	return `<a href="${escapeForMapPopupText(
		detailHref
	)}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 no-underline hover:no-underline"><span class="h5medium text-character-primary leading-snug">${escapeForMapPopupText(
		record.name
	)}</span><span class="${linkColorClass} size-3 inline-flex shrink-0">${EXTERNAL_LINK_ICON_SVG}</span></a>`;
}

function renderAddressHtml(street, cityLine: string): string {
	const streetHtml = street
		? `<div class="bodyregular text-character-secondary leading-snug">${escapeForMapPopupText(
				street
		  )}</div>`
		: '';
	const cityHtml = cityLine
		? `<div class="bodyregular text-character-secondary leading-snug">${escapeForMapPopupText(
				cityLine
		  )}</div>`
		: '';
	return `${streetHtml}${cityHtml}`;
}

function renderPhoneBlockHtml(record): string {
	const formattedPhone = getFormattedLocationPhoneNumber(record);
	if (!formattedPhone) {
		return '';
	}
	return `<div class="bodymedium text-character-primary leading-snug">Phone</div><div class="bodyregular text-character-primary mt-0.5 leading-snug">${escapeForMapPopupText(
		formattedPhone
	)}</div>`;
}

// Stitch the body in order — address, then phone, then contacts — inserting a separator only
// between blocks that are actually present.
function assembleLocationPopupBodyHtml(
	record,
	street,
	cityLine: string,
	variant: PopupContactVariant,
	linkColorClass: string,
	toggleId: string
): string {
	const addressHtml = renderAddressHtml(street, cityLine);
	const phoneHtml = renderPhoneBlockHtml(record);
	const contactsHtml = renderContactsBlockHtml(
		buildPopupContactSections(record, variant),
		linkColorClass,
		toggleId
	);
	const blocks: string[] = [];
	if (addressHtml) {
		blocks.push(addressHtml);
	}
	if (phoneHtml) {
		blocks.push(`${blocks.length ? SEPARATOR_HTML : ''}${phoneHtml}`);
	}
	if (contactsHtml) {
		blocks.push(`${blocks.length ? SEPARATOR_HTML : ''}${contactsHtml}`);
	}
	return blocks.join('');
}

/**
 * Contractor locations use flat `primaryAddress.*` and `longitude`/`latitude` (not nested under
 * `buyerFacility`). Third-party contractors see a single "Client Contact" (facilities-in-charge).
 */
export function buildContractorLocationsMapPopupParts(record): LocationsMapPopupParts {
	const street = nullSafeGet('primaryAddress.streetAddress1', record);
	const cityLine = [
		nullSafeGet('primaryAddress.city', record),
		nullSafeGet('primaryAddress.region', record),
		nullSafeGet('primaryAddress.postcode', record),
	]
		.filter(Boolean)
		.join(' ');

	const rawId = nullSafeGet('id', record);
	const safeId = escapeForMapPopupText(rawId == null ? '' : String(rawId));
	const detailHref = `/supplier/contractor_clients/locations/detail/${safeId}/details`;
	const linkColorClass = 'text-supplier';

	return {
		titleHtml: renderLocationTitleHtml(record, detailHref, linkColorClass),
		bodyHtml: assembleLocationPopupBodyHtml(
			record,
			street,
			cityLine,
			'thirdParty',
			linkColorClass,
			safeId
		),
	};
}

export function buildLocationsMapPopupParts(
	record,
	roleType,
	options: LocationContactGroupsOptions = {}
): LocationsMapPopupParts {
	const street = nullSafeGet('buyerFacility.primaryAddress.streetAddress1', record);
	const cityLine = [
		nullSafeGet('buyerFacility.primaryAddress.city', record),
		nullSafeGet('buyerFacility.primaryAddress.region', record),
		nullSafeGet('buyerFacility.primaryAddress.postcode', record),
	]
		.filter(Boolean)
		.join(' ');
	const isSupplier = roleType === ROLE_TYPES.SUPPLIER;
	const rawId = nullSafeGet('id', record);
	const safeId = escapeForMapPopupText(rawId == null ? '' : String(rawId));
	const detailHref = `${isSupplier ? '/supplier' : '/buyer'}/locations/detail/${safeId}`;
	const linkColorClass = isSupplier ? 'text-supplier' : 'text-buyer';
	// Internal-tech suppliers (showTechnicians) get the technicians view; other suppliers see the
	// client contact, exactly like the contractor popup. Buyers see primary + facilities contacts.
	const variant: PopupContactVariant = isSupplier
		? options.showTechnicians
			? 'supplierInternal'
			: 'thirdParty'
		: 'buyer';

	return {
		titleHtml: renderLocationTitleHtml(record, detailHref, linkColorClass),
		bodyHtml: assembleLocationPopupBodyHtml(
			record,
			street,
			cityLine,
			variant,
			linkColorClass,
			safeId
		),
	};
}
